import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Archive, ArrowLeft } from 'lucide-react';

import ConfirmDialog from '../Elements/ConfirmDialog';
import { avisarSmackCambiado } from '../../lib/smackBus';
import useSmackPolling from './useSmackPolling';
import MessageList from './MessageList';
import MessageComposer from './MessageComposer';

const MESSAGES_POLL_MS = 5000; // mismo régimen que la Barra (comandero)
const PAGE_SIZE = 50;

/**
 * Un canal abierto: historial + envío. Tiempo real v1 por polling de deltas
 * (`after_seq`): el 95% de los sondeos vuelve vacío y no re-descarga nada.
 * Limitación asumida v1: ediciones/borrados de OTROS no llegan por el delta
 * (mismo seq); aparecen al reabrir el canal. Los propios se aplican en local.
 */
export default function ChannelView({ channel, token, myUserId, onBack, isSuperadmin, onArchivado, onVerVideo }) {
  const [messages, setMessages] = useState([]);
  const [hayMas, setHayMas] = useState(false);
  const [cargandoAntiguos, setCargandoAntiguos] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [editando, setEditando] = useState(null); // {id, body}
  const [borrando, setBorrando] = useState(null); // msg
  const [archivando, setArchivando] = useState(false);
  const [confirmarArchivar, setConfirmarArchivar] = useState(false);
  const [error, setError] = useState('');
  const maxSeqRef = useRef(0);
  const leidoHastaRef = useRef(0);
  // El sondeo espera a la carga inicial: sin esto, un tick temprano con
  // after_seq=0 traería los 50 mensajes MÁS ANTIGUOS (orden ascendente) en un
  // canal largo, y la vista arrancaría por el principio del historial.
  const inicialCargadoRef = useRef(false);
  // server_time de la última respuesta: viaja como changed_since en el sondeo
  // para que las ediciones/borrados de OTROS (que no suben seq) lleguen sin
  // recargar. Hora del servidor: inmune al desfase de reloj del cliente.
  const serverTimeRef = useRef(null);

  const marcarLeido = useCallback(
    (seq) => {
      if (!seq || seq <= leidoHastaRef.current) return;
      leidoHastaRef.current = seq;
      axios
        .post(`/v1/smack/channels/${channel.id}/read`, { last_read_seq: seq }, { headers: { Authorization: `Bearer ${token}` } })
        .then(() => avisarSmackCambiado())
        .catch(() => {});
    },
    [channel.id, token]
  );

  const aplicarNuevos = useCallback(
    (nuevos) => {
      if (!nuevos.length) return;
      setMessages((prev) => {
        // Fusión por id: lo nuevo se añade y una edición/borrado (mismo seq,
        // llega por changed_since) REEMPLAZA su versión anterior en sitio.
        const porId = new Map(prev.map((m) => [m.id, m]));
        nuevos.forEach((m) => porId.set(m.id, m));
        return [...porId.values()].sort((a, b) => a.seq - b.seq);
      });
      const maxSeq = Math.max(...nuevos.map((m) => m.seq));
      if (maxSeq > maxSeqRef.current) maxSeqRef.current = maxSeq;
      // Con el canal abierto y la pestaña visible, lo nuevo cuenta como leído
      // (marcarLeido ya ignora repetidos vía leidoHastaRef).
      if (document.visibilityState === 'visible') marcarLeido(maxSeqRef.current);
    },
    [marcarLeido]
  );

  // Carga inicial del canal.
  useEffect(() => {
    let cancelado = false;
    axios
      .get(`/v1/smack/channels/${channel.id}/messages`, {
        params: { limit: PAGE_SIZE },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        if (cancelado) return;
        const lista = r.data?.messages || [];
        setMessages(lista);
        setHayMas(Boolean(r.data?.has_more));
        maxSeqRef.current = lista.length ? lista[lista.length - 1].seq : 0;
        serverTimeRef.current = r.data?.server_time || null;
        inicialCargadoRef.current = true;
        marcarLeido(maxSeqRef.current);
      })
      .catch((err) => {
        if (!cancelado) setError(err?.response?.data?.detail || 'No se pudo cargar el canal.');
      });
    return () => {
      cancelado = true;
    };
  }, [channel.id, token, marcarLeido]);

  // Delta del polling: solo lo posterior a lo ya visto.
  const sondear = useCallback(() => {
    if (!token || !inicialCargadoRef.current) return;
    axios
      .get(`/v1/smack/channels/${channel.id}/messages`, {
        params: {
          after_seq: maxSeqRef.current,
          changed_since: serverTimeRef.current || undefined,
          limit: PAGE_SIZE,
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        serverTimeRef.current = r.data?.server_time || serverTimeRef.current;
        aplicarNuevos(r.data?.messages || []);
      })
      .catch(() => {});
  }, [channel.id, token, aplicarNuevos]);

  useSmackPolling(sondear, MESSAGES_POLL_MS, Boolean(token));

  const cargarAntiguos = useCallback(async () => {
    if (!messages.length || cargandoAntiguos) return;
    setCargandoAntiguos(true);
    try {
      const r = await axios.get(`/v1/smack/channels/${channel.id}/messages`, {
        params: { before_seq: messages[0].seq, limit: PAGE_SIZE },
        headers: { Authorization: `Bearer ${token}` },
      });
      const antiguos = r.data?.messages || [];
      setHayMas(Boolean(r.data?.has_more));
      if (antiguos.length) setMessages((prev) => [...antiguos, ...prev]);
    } catch {
      /* el siguiente scroll lo reintenta */
    } finally {
      setCargandoAntiguos(false);
    }
  }, [channel.id, token, messages, cargandoAntiguos]);

  const enviar = useCallback(
    async (body, fichero) => {
      setEnviando(true);
      setError('');
      try {
        const formData = new FormData();
        formData.append('body', body);
        if (fichero) formData.append('file', fichero);
        const r = await axios.post(`/v1/smack/channels/${channel.id}/messages`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        aplicarNuevos([r.data]);
        avisarSmackCambiado();
        return true;
      } catch (err) {
        setError(err?.response?.data?.detail || 'No se pudo enviar el mensaje.');
        return false;
      } finally {
        setEnviando(false);
      }
    },
    [channel.id, token, aplicarNuevos]
  );

  const guardarEdicion = useCallback(async () => {
    if (!editando) return;
    try {
      const r = await axios.patch(
        `/v1/smack/messages/${editando.id}`,
        { body: editando.body },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages((prev) => prev.map((m) => (m.id === editando.id ? r.data : m)));
      setEditando(null);
      setError('');
    } catch (err) {
      // El editor se queda ABIERTO con el texto: cerrarlo tiraba a la basura lo
      // que el usuario acababa de reescribir, y encima culpaba a la ventana de
      // 15 minutos aunque el fallo fuese de red o un 500.
      const detalle = err?.response?.data?.detail;
      setError(
        detalle ||
          (err?.response
            ? 'No se pudo editar el mensaje.'
            : 'No se pudo editar: revisa la conexión.')
      );
    }
  }, [editando, token]);

  const flagMessage = useCallback(
    async (msg) => {
      try {
        await axios.post(
          `/v1/smack/messages/${msg.id}/flag`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        avisarSmackCambiado(); // refresca el badge de pendientes del sidebar ya
        return true;
      } catch (err) {
        setError(err?.response?.data?.detail || 'No se pudo marcar el mensaje.');
        return false;
      }
    },
    [token]
  );

  const confirmarBorrado = useCallback(async () => {
    if (!borrando) return;
    try {
      const r = await axios.delete(`/v1/smack/messages/${borrando.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages((prev) => prev.map((m) => (m.id === borrando.id ? r.data : m)));
      avisarSmackCambiado();
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo borrar el mensaje.');
    } finally {
      setBorrando(null);
    }
  }, [borrando, token]);

  const archivarCanal = useCallback(async () => {
    setArchivando(true);
    try {
      await axios.patch(
        `/v1/smack/channels/${channel.id}/archive`,
        { archived: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConfirmarArchivar(false);
      onArchivado?.();
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo archivar el canal.');
      setConfirmarArchivar(false);
    } finally {
      setArchivando(false);
    }
  }, [channel.id, token, onArchivado]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 md:hidden"
          aria-label="Volver a canales"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-gray-900">
            <span className="text-gray-400">#</span> {channel.name}
          </h2>
          {channel.venue_name ? (
            <p className="truncate text-[11px] text-gray-400">{channel.venue_name}</p>
          ) : (
            <p className="truncate text-[11px] text-gray-400">Canal interno del equipo</p>
          )}
        </div>
        {isSuperadmin ? (
          <button
            type="button"
            onClick={() => setConfirmarArchivar(true)}
            className="ml-auto rounded-lg p-1 text-gray-500 hover:bg-gray-100"
            aria-label="Archivar canal"
            title="Archivar canal"
          >
            <Archive size={18} />
          </button>
        ) : null}
      </div>

      {error ? <p className="border-b border-red-100 bg-red-50 px-4 py-1.5 text-xs text-red-700">{error}</p> : null}

      <MessageList
        messages={messages}
        myUserId={myUserId}
        onEditar={(msg) => setEditando({ id: msg.id, body: msg.body })}
        onBorrar={(msg) => setBorrando(msg)}
        onFlag={flagMessage}
        onCargarAntiguos={cargarAntiguos}
        hayMas={hayMas}
        cargandoAntiguos={cargandoAntiguos}
        onVerVideo={onVerVideo}
      />

      {editando ? (
        <div className="border-t border-gray-200 bg-amber-50 p-3">
          <p className="pb-1.5 text-xs font-medium text-amber-700">Editando mensaje</p>
          <div className="flex items-end gap-2">
            <textarea
              value={editando.body}
              onChange={(e) => setEditando((prev) => ({ ...prev, body: e.target.value }))}
              rows={2}
              className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#FF5400] focus:outline-none"
            />
            {/* #FF5400: acento de marca de Smack (decisión 22-ago: se queda) — fuera de tokens.css a propósito: el design system es slate y este naranja es exclusivo del módulo. */}
            <button
              type="button"
              onClick={guardarEdicion}
              className="rounded-lg bg-[#FF5400] px-3 py-2 text-sm font-medium text-white"
            >
              Guardar
            </button>
            <button
              type="button"
              onClick={() => setEditando(null)}
              className="rounded-lg px-3 py-2 text-sm text-gray-500 hover:bg-gray-100"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <MessageComposer onEnviar={enviar} enviando={enviando} />
      )}

      <ConfirmDialog
        active={Boolean(borrando)}
        title="¿Borrar este mensaje?"
        description="Quedará como «mensaje eliminado» para todos."
        confirmLabel="Borrar"
        tone="danger"
        onConfirm={confirmarBorrado}
        onCancel={() => setBorrando(null)}
      />

      <ConfirmDialog
        active={confirmarArchivar}
        title={`¿Archivar #${channel.name}?`}
        description="Desaparecerá de la lista para todos. El historial no se borra y se puede recuperar."
        confirmLabel="Archivar"
        busy={archivando}
        onConfirm={archivarCanal}
        onCancel={() => setConfirmarArchivar(false)}
        titleId="smack-archivar-canal"
      />
    </div>
  );
}
